import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function AssetsSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    api
      .get("/assets/settings")
      .then(({ data }) => setSettings(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const changeCurrency = async (base_currency) => {
    setSaving(true);
    try {
      await api.put("/assets/settings", { base_currency });
      setSettings((s) => ({ ...s, base_currency }));
      toast.success(`Base currency set to ${base_currency}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const clearHistory = async () => {
    setClearing(true);
    try {
      await api.delete("/assets/snapshots");
      toast.success("History cleared. A fresh baseline was recorded.");
      setConfirmOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClearing(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-8" data-testid="assets-settings">
      <section className="rounded-xl border border-border bg-card p-6">
        <h2 className="font-serif text-xl text-foreground mb-1">Base currency</h2>
        <p className="text-sm text-muted-foreground mb-4">
          All holdings are converted to this currency for totals and charts.
        </p>
        <div className="flex flex-wrap gap-2">
          {settings.supported_currencies.map((c) => (
            <button
              key={c}
              disabled={saving}
              onClick={() => changeCurrency(c)}
              data-testid={`currency-${c}`}
              className={`px-3.5 py-2 rounded-md text-sm font-medium border transition-colors duration-200 ${
                settings.base_currency === c
                  ? "bg-foreground text-background border-foreground"
                  : "bg-white/70 text-foreground/80 border-input hover:border-foreground/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-destructive/30 bg-destructive/5 p-6" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4 text-destructive" />
          <h2 className="font-serif text-xl text-foreground">Danger zone</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Clear history permanently deletes every recorded net worth snapshot. Your holdings are
          not touched, and a fresh baseline is recorded immediately.
        </p>
        <button
          onClick={() => setConfirmOpen(true)}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-destructive text-destructive-foreground font-medium hover:bg-destructive/90 transition-colors duration-200"
          data-testid="clear-history-btn"
        >
          <Trash2 className="w-4 h-4" /> Clear history
        </button>
      </section>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="clear-history-modal">
          <DialogHeader>
            <DialogTitle>Clear all history?</DialogTitle>
            <DialogDescription>
              This deletes every net worth snapshot permanently. This cannot be undone. Your
              holdings will not be affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              onClick={() => setConfirmOpen(false)}
              className="px-4 py-2 rounded-md text-foreground/80 hover:bg-secondary transition-colors duration-200"
              data-testid="clear-history-cancel"
            >
              Cancel
            </button>
            <button
              onClick={clearHistory}
              disabled={clearing}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-destructive text-destructive-foreground font-medium hover:bg-destructive/90 disabled:opacity-60 transition-colors duration-200"
              data-testid="clear-history-confirm"
            >
              {clearing && <Loader2 className="w-4 h-4 animate-spin" />} Yes, clear it
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
